import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';
import { Menu, Transition } from '@headlessui/react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import React, { Fragment, useMemo, useState } from 'react';
import { ChevronDownIcon, TrashIcon } from '@heroicons/react/outline';
import { Link } from 'react-router-dom';
import useElementTruncated from '../utils/useElementTruncated';
import Tooltip from '../common/components/Tooltip';
import Button from './Button';
import BetaBadge from './BetaBadge';

const MenuItem = ({
  testId,
  className,
  label,
  icon,
  onClick,
  disabled,
  isActive,
  onRemove,
  to,
  handleMouseLeave,
}) => {
  const { ref, isElementTruncated } = useElementTruncated();

  let Component = 'button';
  let componentProps = {
    type: 'button',
    disabled,
    onClick,
  };

  if (to) {
    Component = Link;
    componentProps = {
      to,
    };
  }

  return (
    <Menu.Item data-testid={testId} as="li" disabled={disabled}>
      <div className="cluster-1">
        <Component
          className={`${className} ${
            isActive
              ? 'bg-neutral-500 dark:bg-neutral-800'
              : !disabled && 'hover:bg-neutral-500 dark:hover:bg-neutral-800'
          } cluster-2 items-center text-start w-full rounded-md px-2 py-2 text-sm disabled:cursor-not-allowed disabled:text-on-disabled ${
            to
              ? 'text-body hover:text-body focus:text-body hover:no-underline focus:no-underline'
              : ''
          }`}
          onClick={to ? handleMouseLeave : onClick}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...componentProps}
        >
          {icon}
          <Tooltip
            title={isElementTruncated ? label : ''}
            containerClassName="truncate"
          >
            <span ref={ref} className="truncate">
              {label}
            </span>
          </Tooltip>
        </Component>
        {onRemove && (
          <button
            data-testid="dropdown-item-remove"
            type="button"
            className="hover:bg-danger-500 hover:text-on-danger-500 rounded-md px-2 py-2"
            onClick={onRemove}
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        )}
      </div>
    </Menu.Item>
  );
};

MenuItem.propTypes = {
  testId: PropTypes.string,
  className: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  icon: PropTypes.node,
  onClick: PropTypes.func,
  onRemove: PropTypes.func,
  disabled: PropTypes.bool,
  isActive: PropTypes.bool,
  to: PropTypes.string,
};
MenuItem.defaultProps = {
  testId: null,
  className: '',
  label: null,
  icon: null,
  onClick: () => {},
  onRemove: null,
  disabled: false,
  isActive: false,
  to: null,
};

// TODO: Should we add aria-hidden="true" to buttons?
const Dropdown = ({
  'data-testid': testId,
  as,
  asProps,
  title,
  label,
  icon,
  type,
  position,
  items,
  activeId,
  isBeta,
}) => {
  const { t } = useTranslation();

  const [isHover, setIsHover] = useState(false);

  const handleMouseEnter = () => setIsHover(true);
  const handleMouseLeave = () => setIsHover(false);

  const menuItems = useMemo(() => {
    const hasNestedItems = items.some((elem) => isArray(elem));

    if (hasNestedItems) {
      return items.map((nestedItems, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <ul key={i} className="p-1 stack-1">
          {nestedItems.map((item, j) => {
            const props = {
              ...item,
              handleMouseLeave,
              isActive: activeId && activeId === item.id,
            };
            // eslint-disable-next-line react/jsx-props-no-spreading, react/no-array-index-key
            return <MenuItem key={j} {...props} />;
          })}
        </ul>
      ));
    }

    return (
      <ul className="p-1 stack-1">
        {items.map((item, i) => {
          const props = {
            ...item,
            handleMouseLeave,
            isActive: activeId && activeId === item.id,
          };
          // eslint-disable-next-line react/jsx-props-no-spreading, react/no-array-index-key
          return <MenuItem key={i} {...props} />;
        })}
      </ul>
    );
  }, [activeId, items]);

  const menuItemsPosition = useMemo(() => {
    switch (position) {
      case 'bottom-end':
        return 'inline-end-0 pbs-2';
      case 'right-start':
        return 'block-start-0 inline-start-full pis-2';
      case 'right-end':
        return 'block-end-0 inline-start-full pis-2';
      default:
        return '';
    }
  }, [position]);

  const menuButtonProps = as
    ? { icon, label, ...asProps }
    : {
        type,
        'data-testid': testId,
        icon: icon || <ChevronDownIcon className="h-5 w-5" />,
      };

  return (
    <Menu
      as="div"
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {() => (
        <>
          <div>
            <Menu.Button
              as={as || Button}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...menuButtonProps}
            >
              {label}
            </Menu.Button>
          </div>
          <Transition
            as={Fragment}
            show={isHover}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            {/* FIXME: Double check menu.items styling */}
            <div
              className={`absolute z-10 w-56 ${menuItemsPosition} origin-top-right`}
            >
              <Menu.Items
                className="divide-y divide-gray-100 rounded-md bg-surface shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                static
              >
                {title && (
                  <div
                    className={`py-2 px-3 ${
                      isBeta ? 'cluster-3 items-center' : ''
                    }`}
                  >
                    <span className="text-sm font-semibold">{title}</span>
                    {isBeta && <BetaBadge />}
                  </div>
                )}
                {isEmpty(items) ? (
                  <ul className="p-1 stack-1">
                    <MenuItem
                      label={t('empty')}
                      className="justify-center"
                      handleMouseLeave={handleMouseLeave}
                      disabled
                    />
                  </ul>
                ) : (
                  menuItems
                )}
              </Menu.Items>
            </div>
          </Transition>
        </>
      )}
    </Menu>
  );
};

const DropdownItem = PropTypes.shape({
  testId: PropTypes.string,
  id: PropTypes.string,
  className: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  icon: PropTypes.node,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
});

Dropdown.propTypes = {
  'data-testid': PropTypes.string,
  as: PropTypes.elementType,
  // eslint-disable-next-line react/forbid-prop-types
  asProps: PropTypes.object,
  title: PropTypes.string,
  label: PropTypes.string,
  icon: PropTypes.node,
  // FIXME: Change to oneOf
  type: PropTypes.string,
  position: PropTypes.oneOf(['bottom-end', 'right-start', 'right-end']),
  items: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.arrayOf(DropdownItem), DropdownItem]),
  ),
  activeId: PropTypes.string,
  isBeta: PropTypes.bool,
};

Dropdown.defaultProps = {
  'data-testid': 'dropdown',
  as: null,
  asProps: {},
  title: null,
  label: null,
  icon: null,
  type: 'secondary',
  position: 'bottom-end',
  items: [],
  activeId: null,
  isBeta: false,
};

export default Dropdown;
